import { useCallback } from "react";

import { type AdminProfile, type UUID } from "@/core/types";
import { DashboardCard2 } from "@/components/layout/DashboardCard2";
import { Heading2 } from "@/ui2/atoms/Heading";
import { Switch2 } from "@/ui2/molecules/Switch";
import { useUpdateLcswPermission } from "@/features/permissions/api/mutations/useUpdateLcswPermission";

type LcswPermissionCardProps = {
  isLcsw: boolean;
  isReadOnly?: boolean;
  userId: UUID;
  userName: AdminProfile["name"];
};

export function LcswPermissionCard({
  isLcsw,
  isReadOnly,
  userId,
  userName,
}: LcswPermissionCardProps) {
  const { isPending, mutate } = useUpdateLcswPermission({ userId, userName });

  // Guard re-entrancy in the handler instead of visually disabling the switch when editable —
  // Switch2 renders disabled+selected in a neutral gray, which would mask the instant optimistic
  // update. When isReadOnly is explicitly set (e.g. viewing own profile), the gray state is
  // correct: the switch is genuinely non-interactive and Switch2 blocks onChange natively.
  const handleChange = useCallback(
    (value: boolean) => {
      if (isPending) return;

      mutate({ lcsw: { is_lcsw: value } });
    },
    [isPending, mutate],
  );

  return (
    <DashboardCard2 classNameChildren="tw:flex tw:flex-col tw:gap-4" title="Extended Permissions">
      <Switch2 isDisabled={isReadOnly} isSelected={isLcsw} onChange={handleChange}>
        <div className="tw:flex tw:flex-col tw:gap-2">
          <Heading2 as="h3" size={1}>
            Co-Sign Supervision
          </Heading2>

          <p className="body-md">Enables co-signing and rejecting LMSW notes.</p>
        </div>
      </Switch2>
    </DashboardCard2>
  );
}
